/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, October 27th 2021, 1:21:45 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  OSAPIForm,
  TextFormInput,
  CardWidget,
  TitleTypography,
  HelperText,
  BodyTypography,
  PaddedDivider,
  ImageUploadFormInput,
  Responsive4ColGridContainer,
  Responsive4ColGridItem,
  OSAvatar,
  HorizontalStackWidget,
  Divider,
} from "ui-widgets";
import { Organization, newOrganizationDefault } from "lib/schemas";
import { DepartmentNestedPopUpField } from "organizations/components/DepartmentNestedPopUpField";
import { IndustryPickerInput } from "core/components/IndustryPickerInput";
import { CountryPickerInput } from "core/components/CountryPickerInput";
import { CurrencyPickerInput } from "core/components/CurrencyPickerInput";
import BusinessIcon from "@mui/icons-material/Business";
// import { WorkersTransferListInput } from "workers";
import { useGlobalScope } from "core/providers";
import { useOrganizationAPIForm } from "organizations/services/OrganizationsAPI";

export const OrganizationForm = (props: { organizationId: Organization["id"] }) => {
  const { loggedInUser } = useGlobalScope().userContext;

  const [organization, setOrganization] = React.useState<Organization>({
    ...newOrganizationDefault,
    organization_admins: [loggedInUser?.id],
    workers: [loggedInUser?.id],
  });

  const { watched_organization_name, useAPIFormReturn, newDepartmentDefault } = useOrganizationAPIForm({
    organization,
    setOrganization,
    organizationInitialFetchId: props.organizationId,
  });

  return (
    <CardWidget>
      <TitleTypography>Organization Details</TitleTypography>
      <HelperText>Provide information about your organization.</HelperText>
      <PaddedDivider />
      <HorizontalStackWidget showDivider>
        <OSAvatar name={watched_organization_name} imageSrc={organization?.logo}>
          <BusinessIcon />
        </OSAvatar>
        {organization?.name ? (
          <React.Fragment>
            <BodyTypography>
              <b>{organization?.name}</b>
            </BodyTypography>
            <BodyTypography>{organization?.email}</BodyTypography>
            <BodyTypography>{organization?.phone}</BodyTypography>
          </React.Fragment>
        ) : null}
      </HorizontalStackWidget>

      <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton" {...props}>
        <Responsive4ColGridContainer>
          <Responsive4ColGridItem>
            <PaddedDivider />
            <BodyTypography>General Details</BodyTypography>
            <TextFormInput<Organization> schema_field_key="name" label="Name" />
            <TextFormInput<Organization> schema_field_key="slogan" label="Slogan" />
            <IndustryPickerInput<Organization> schema_field_key="sector" />
          </Responsive4ColGridItem>
          <Responsive4ColGridItem>
            <PaddedDivider />
            <BodyTypography>Contact Information</BodyTypography>
            <TextFormInput<Organization> schema_field_key="email" label="Email" />
            <TextFormInput<Organization> schema_field_key="phone" label="Phone Number" />
            <TextFormInput<Organization> schema_field_key="postal_address" label="Postal Address" />
            <TextFormInput<Organization> schema_field_key="website" label="Website" placeholder="https://mysite.com" />
          </Responsive4ColGridItem>
          <Responsive4ColGridItem>
            <PaddedDivider />
            <BodyTypography>Physical Location</BodyTypography>
            <CountryPickerInput<Organization> schema_field_key="country" />
            <TextFormInput<Organization> schema_field_key="city" label="City" />
            <TextFormInput<Organization> schema_field_key="street" label="Street" />
            <TextFormInput<Organization> schema_field_key="office" label="Office Location" />
          </Responsive4ColGridItem>
          <Responsive4ColGridItem>
            <PaddedDivider />
            <BodyTypography>Other Information</BodyTypography>
            <CurrencyPickerInput<Organization> schema_field_key="default_currency" label="Default Currency" />
          </Responsive4ColGridItem>
        </Responsive4ColGridContainer>
        <PaddedDivider />
        <BodyTypography>Logo</BodyTypography>
        <ImageUploadFormInput<Organization> schema_field_key="logo" label="Logo" />
        <PaddedDivider />
        <BodyTypography>Departments</BodyTypography>
        <DepartmentNestedPopUpField
          schema_field_key="department_set"
          newDepartmentDefault={newDepartmentDefault}
          label={`Departments${watched_organization_name && ` For ${watched_organization_name}`}`}
        />

        <Divider />
        {/* Commenting below since we now have a specific function to convert a worker to an administrator under the workers module.  */}
        {/* <WorkersTransferListInput<Organization> label="Administrators" schema_field_key="organization_admins" /> */}
      </OSAPIForm>
    </CardWidget>
  );
};
